/**
 * 交易详情API服务
 * 简单的真实接口请求方法
 */

import { getDepositRecordsByTimeRange, getWithdrawRecordsByTimeRange, speedUpDeposit, queryOrder, deleteTransactionMessage } from '@/api/transaction'
import type { TransactionDepositApiResponse, TransactionWithdrawApiResponse } from '@/api/transaction/types'
import { ERROR_CODES } from '@/enums'

/**
 * 获取充值交易记录
 * @param start 开始时间 YYYY-MM-DD HH:mm:ss
 * @param end 结束时间 YYYY-MM-DD HH:mm:ss
 * @returns Promise<TransactionDepositApiResponse>
 */
export const fetchTransactionDepositRecords = async (
    days: number,
    from: number,
    size: number,
    options?: object
): Promise<TransactionDepositApiResponse> => {
    try {
        const response = await getDepositRecordsByTimeRange({ days, from, size }, false, options)

        // 确保响应格式符合接口要求
        if (response.code === 200 && response.data) {
            return {
                code: response.code,
                data: response.data || [],
            }
        } else {
            throw new Error(`API响应异常: code=${response.code}`)
        }
    } catch (error) {
        throw error
    }
}

/**
 * 获取提现交易记录
 * @param start 开始时间 YYYY-MM-DD HH:mm:ss
 * @param end 结束时间 YYYY-MM-DD HH:mm:ss
 * @returns Promise<TransactionWithdrawApiResponse>
 */
export const fetchTransactionWithdrawRecords = async (
    days: number,
    from: number,
    size: number,
    options?: object
): Promise<TransactionWithdrawApiResponse> => {
    try {
        const response = await getWithdrawRecordsByTimeRange({ days, from, size }, false, options)

        // 确保响应格式符合接口要求
        if (response.code === 200 && response.data) {
            return {
                code: response.code,
                data: {
                    total: response.data.total,
                    max_score: response.data.max_score,
                    hits: response.data.hits || [],
                },
            }
        } else {
            throw new Error(`API响应异常: code=${response.code}`)
        }
    } catch (error) {
        throw error
    }
}

/**
 * 充值订单加速
 * @param orderid 订单ID
 * @returns Promise<{ success: boolean, at?: string, message?: string }>
 */
export const speedUpDepositOrder = async (orderid: string): Promise<{ success: boolean; at?: string; message?: string }> => {
    try {
        const response = await speedUpDeposit({ orderid }, false)

        // 检查响应是否成功
        if (response.code === ERROR_CODES.OK) {
            return {
                success: true,
                at: response.at,
                message: '加速成功',
            }
        } else {
            return {
                success: false,
                message: `加速失败: code=${response.code}`,
            }
        }
    } catch (error) {
        return {
            success: false,
            message: error instanceof Error ? error.message : '加速请求失败',
        }
    }
}

/**
 * 订单查询
 * @param type 订单类型
 * @param orderid 订单ID
 * @returns Promise<{ success: boolean, content?: any, message?: string }>
 */
export const queryOrderDetails = async (type: string, orderid: string, loading = true): Promise<{ success: boolean; content?: any }> => {
    try {
        const response = await queryOrder({ type, orderid }, loading)

        // 检查响应是否成功
        if (response.code === ERROR_CODES.OK) {
            return {
                success: true,
                content: response.content,
            }
        } else {
            return {
                success: false,
            }
        }
    } catch (error) {
        return {
            success: false,
        }
    }
}

/**
 * 交易消息删除
 * @param channle 频道
 * @param id 消息ID
 * @returns Promise<{ success: boolean }>
 */
export const deleteTransactionMessageApi = async (channle: string, id: string): Promise<{ success: boolean }> => {
    try {
        const response = await deleteTransactionMessage({ channle, id }, true)

        // 检查响应是否成功
        if (response.code === ERROR_CODES.OK) {
            return {
                success: true,
            }
        }
    } catch (error) {
        return {
            success: false,
        }
    }
}
